"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import css from "./WorkspaceListFilter.module.css";

export function WorkspaceListFilter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string; count: number }[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  const current = options.find((option) => option.value === value);
  function close(restore = false) {
    setOpen(false);
    if (restore) trigger.current?.focus();
  }
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  return (
    <div className={css.root} ref={root}>
      <button
        className={css.trigger}
        ref={trigger}
        type="button"
        aria-label={`${label}: ${current?.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={css.label}>{label}:</span>
        <span className={css.value}>{current?.label}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          id={id}
          className={css.menu}
          role="menu"
          aria-label={`Filter by ${label.toLowerCase()}`}
          onBlur={(event) => {
            if (!root.current?.contains(event.relatedTarget as Node)) close();
          }}
          onKeyDown={(event) => {
            const items = [
              ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
                '[role="menuitemradio"]',
              ),
            ];
            const index = items.indexOf(
              document.activeElement as HTMLButtonElement,
            );
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              close(true);
            } else if (event.key === "Tab") close(true);
            else if (
              ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
            ) {
              event.preventDefault();
              const next =
                event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? items.length - 1
                    : (index +
                        (event.key === "ArrowDown" ? 1 : -1) +
                        items.length) %
                      items.length;
              items[next]?.focus();
            } else if (
              event.key.length === 1 &&
              event.key !== " " &&
              !event.metaKey &&
              !event.ctrlKey
            ) {
              const match = [
                ...items.slice(index + 1),
                ...items.slice(0, index + 1),
              ].find((item) =>
                item.textContent
                  ?.trim()
                  .toLowerCase()
                  .startsWith(event.key.toLowerCase()),
              );
              if (match) {
                event.preventDefault();
                match.focus();
              }
            }
          }}
        >
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              role="menuitemradio"
              aria-label={`${option.label} ${option.count}`}
              aria-checked={value === option.value}
              tabIndex={-1}
              onClick={() => {
                onChange(option.value);
                close(true);
              }}
            >
              <span className={css.check}>
                {value === option.value && <Check size={14} />}
              </span>
              <span>{option.label}</span>
              <span className={css.count}>{option.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
